import { DEFAULT_SUGGESTED_AMOUNTS_COUNT, ProductDonationRecurrenceInterval } from "@simplyk/common";
import { v4 } from "uuid";

import {
  DONATION_FREQUENCIES,
  getFrequencyConfig,
  getSavedFrequencyAmounts,
  inferSuggestedAmountsCount,
  isFrequencyEnabledByDefault,
  resizeAmounts,
} from "./donationFrequencies";

import { DonationFormWithUserOrOrgAccessOutput } from "@/common/types/trpc";
import {
  Amount,
  DefaultFrequencyValue,
  FrequencyAmountsName,
  FrequencySwitchName,
  StepSuggestedDonationAmountsFormData,
} from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/types";

export const useGetDefaultValue = ({
  apiFormObject,
}: {
  apiFormObject: DonationFormWithUserOrOrgAccessOutput;
}): StepSuggestedDonationAmountsFormData => {
  const { isAmountsAlreadySavedInCreation } = apiFormObject;
  const defaultAmountsFrequency: DefaultFrequencyValue =
    apiFormObject?.defaultAmountsFrequency ?? ProductDonationRecurrenceInterval.OneTime;

  if (!isAmountsAlreadySavedInCreation) {
    const suggestedAmountsCount = DEFAULT_SUGGESTED_AMOUNTS_COUNT;
    return {
      ...buildFrequencyValues((frequency) => ({
        isEnabled: isFrequencyEnabledByDefault(frequency.interval),
        amounts: resizeAmounts(
          generateDefaultAmounts(
            frequency.interval,
            frequency.interval === ProductDonationRecurrenceInterval.OneTime
              ? (apiFormObject?.prefillOneTimeAmounts ?? undefined)
              : undefined
          ),
          frequency.interval,
          suggestedAmountsCount
        ),
      })),
      suggestedAmountsCount,
      preselectThirdAmount: apiFormObject?.preselectThirdAmount ?? true,
      defaultAmountsFrequency,
      enableAmountLabels: false,
      promoteMonthlyGiving: true,
      smartAmountsEnabled: apiFormObject?.smartAmountsEnabled ?? true,
    };
  }

  const localeAmounts =
    apiFormObject?.donationFormFields?.find((donationFormField) => donationFormField.locale === apiFormObject.locale)
      ?.donationFormAmounts ?? [];
  const suggestedAmountsCount = inferSuggestedAmountsCount(localeAmounts);

  const frequencyValues = buildFrequencyValues((frequency) => {
    const amounts = localeAmounts.filter(
      (donationFormAmount) => donationFormAmount.recurrenceInterval === frequency.interval
    ) as Amount[];
    return {
      isEnabled: amounts.length > 0,
      amounts: getSavedFrequencyAmounts(amounts, frequency.interval, suggestedAmountsCount),
    };
  });

  const hasAnyAmountLabel = DONATION_FREQUENCIES.some((frequency) =>
    frequencyValues[frequency.amountsName].some((amount) => Boolean(amount.message?.trim()))
  );

  return {
    ...frequencyValues,
    suggestedAmountsCount,
    preselectThirdAmount: apiFormObject?.preselectThirdAmount ?? true,
    defaultAmountsFrequency,
    enableAmountLabels: hasAnyAmountLabel,
    promoteMonthlyGiving: apiFormObject?.promoteMonthlyGiving ?? false,
    smartAmountsEnabled: apiFormObject?.smartAmountsEnabled ?? true,
  };
};

const buildFrequencyValues = (
  getFrequencyValue: (frequency: (typeof DONATION_FREQUENCIES)[number]) => { isEnabled: boolean; amounts: Amount[] }
): Record<FrequencySwitchName, boolean> & Record<FrequencyAmountsName, Amount[]> =>
  Object.fromEntries(
    DONATION_FREQUENCIES.flatMap((frequency) => {
      const { isEnabled, amounts } = getFrequencyValue(frequency);
      return [
        [frequency.switchName, isEnabled],
        [frequency.amountsName, amounts],
      ];
    })
  ) as Record<FrequencySwitchName, boolean> & Record<FrequencyAmountsName, Amount[]>;

export const generateDefaultAmounts = (
  recurrenceInterval: ProductDonationRecurrenceInterval,
  prefillOneTimeAmounts?: number[]
): Amount[] => {
  const amounts = prefillOneTimeAmounts?.length
    ? prefillOneTimeAmounts
    : getFrequencyConfig(recurrenceInterval).defaultAmounts;

  return amounts.map((amount, index) => ({
    amount,
    message: "",
    isRecurrent: false,
    sortIndex: index,
    recurrenceInterval,
    id: v4(),
  }));
};
